import React, { useState, useEffect, useRef } from "react";
import { InvitationCodeType } from "types/InvitationCodeTypes";
import InputValidationTooltip from "components/common/InputValidationTooltip";
import { nonDigitNorAlphaGlobalRegex } from "constants/validations";

interface InvitationCodeFormProps {
    error?: boolean;
    errorType?: InvitationCodeType;
    onValueChange?: (value: string) => void;
    onInputChange?: () => void;
    initialValue?: string;
}

export const InvitationCodeForm = ({
    error,
    errorType,
    onValueChange,
    onInputChange,
    initialValue = "",
}: InvitationCodeFormProps) => {
    const [invitationCode, setInvitationCode] = useState(initialValue);
    const [showTooltip, setShowTooltip] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const originalValue = e.target.value;
        const hasInvalidChars = nonDigitNorAlphaGlobalRegex.test(originalValue);

        if (hasInvalidChars && originalValue.length > 0) {
            inputRef.current = e.target;
            setShowTooltip(true);

            if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);

            tooltipTimeoutRef.current = setTimeout(() => {
                setShowTooltip(false);
            }, 2000);
        }

        const filteredValue = originalValue.replace(nonDigitNorAlphaGlobalRegex, "");
        const upperCaseValue = filteredValue.toUpperCase();
        const limitedValue = upperCaseValue.slice(0, 10);

        setInvitationCode(limitedValue);

        if (onInputChange) {
            onInputChange();
        }

        if (onValueChange) {
            onValueChange(limitedValue);
        }
    };

    useEffect(() => {
        return () => {
            if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
        };
    }, []);

    const getInputClasses = () => {
        const baseClasses = "input h-[36px] input-bordered w-full";

        if (error) {
            return `${baseClasses} input-error bg-base-100`;
        }

        return baseClasses;
    };

    const getErrorText = () => {
        return errorType === "minCharacters" ? "Invitation code must be 10 characters" : "Invitation code is invalid";
    };

    return (
        <div className="flex flex-col gap-1">
            <label htmlFor="invitationCode" className="font-semibold text-sm leading-5 text-base-content pl-1">
                10-Character Invitation Code
            </label>
            <input
                ref={inputRef}
                id="invitationCode"
                type="text"
                name="invitationCode"
                data-testid="test-invitationCode"
                placeholder="M0-12345678"
                className={getInputClasses()}
                value={invitationCode}
                onChange={handleInputChange}
                maxLength={10}
            />
            <InputValidationTooltip message="Only letters and numbers allowed" show={showTooltip} targetRef={inputRef} />
            {error && <p className="text-xs font-medium text-error pl-1">{getErrorText()}</p>}
        </div>
    );
};
